<template>
  <div class="header">
    <div class="header__left">
      <!-- 折叠按钮 -->
      <i
        class="header__collapse-btn"
        :class="isCollapsed ? 'el-icon-s-unfold' : 'el-icon-s-fold'"
        @click="handleToggleSidebar"
      ></i>
      <!-- 面包屑导航 -->
      <el-breadcrumb separator="/" class="header__breadcrumb">
        <el-breadcrumb-item v-for="item in breadcrumbs" :key="item.path">
          <span v-if="item.path === $route.path" class="header__breadcrumb-current">{{ item.meta.title }}</span>
          <span v-else>{{ item.meta.title }}</span>
        </el-breadcrumb-item>
      </el-breadcrumb>
    </div>
    <div class="header__right">
      <!-- 未读通知角标 -->
      <el-badge :value="unreadCount" :hidden="unreadCount === 0" :max="99" class="header__notify">
        <i class="el-icon-bell header__notify-icon" @click="handleGoNotification"></i>
      </el-badge>
      <!-- 用户信息下拉 -->
      <el-dropdown trigger="click" @command="handleCommand">
        <span class="header__user">
          <i class="el-icon-user-solid header__user-avatar"></i>
          <span class="header__user-name">{{ userName }}</span>
          <i class="el-icon-arrow-down"></i>
        </span>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item command="password">
            <i class="el-icon-key"></i> 修改密码
          </el-dropdown-item>
          <el-dropdown-item command="logout" divided>
            <i class="el-icon-switch-button"></i> 退出登录
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>

    <!-- 修改密码弹窗 -->
    <el-dialog title="修改密码" :visible.sync="passwordDialogVisible" width="420px" @closed="handlePasswordDialogClosed">
      <el-form ref="passwordForm" :model="passwordForm" :rules="passwordRules" label-width="90px">
        <el-form-item label="原密码" prop="oldPassword">
          <el-input v-model="passwordForm.oldPassword" type="password" placeholder="请输入原密码" show-password></el-input>
        </el-form-item>
        <el-form-item label="新密码" prop="newPassword">
          <el-input v-model="passwordForm.newPassword" type="password" placeholder="请输入新密码" show-password></el-input>
        </el-form-item>
        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input v-model="passwordForm.confirmPassword" type="password" placeholder="请再次输入新密码" show-password></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="passwordDialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="submitLoading" @click="handleChangePassword">确 定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { changePassword } from '@/api/admin/auth'
import { getNotificationUnreadCount } from '@/api/admin/notifications'

export default {
  name: 'AppHeader',
  data() {
    const validateConfirm = (rule, value, callback) => {
      if (value !== this.passwordForm.newPassword) {
        callback(new Error('两次输入的密码不一致'))
      } else {
        callback()
      }
    }
    return {
      // 未读通知数量
      unreadCount: 0,
      // 未读通知轮询定时器
      unreadTimer: null,
      passwordDialogVisible: false,
      submitLoading: false,
      passwordForm: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      passwordRules: {
        oldPassword: [
          { required: true, message: '请输入原密码', trigger: 'blur' }
        ],
        newPassword: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, max: 20, message: '密码长度为 6-20 位', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请再次输入新密码', trigger: 'blur' },
          { validator: validateConfirm, trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    /**
     * 侧边栏是否折叠
     */
    isCollapsed() {
      return this.$store.state.app.sidebarCollapsed
    },
    /**
     * 当前用户名
     */
    userName() {
      const userInfo = this.$store.state.user.userInfo
      return (userInfo && (userInfo.real_name || userInfo.username)) || '管理员'
    },
    /**
     * 面包屑导航列表
     */
    breadcrumbs() {
      const matched = this.$route.matched.filter(item => item.meta && item.meta.title)
      return matched
    }
  },
  created() {
    this.fetchUnreadCount()
    // 每60秒轮询一次未读数量
    this.unreadTimer = setInterval(() => {
      this.fetchUnreadCount()
    }, 60000)
  },
  beforeDestroy() {
    // 组件销毁时清除定时器，避免内存泄漏
    if (this.unreadTimer) {
      clearInterval(this.unreadTimer)
      this.unreadTimer = null
    }
  },
  methods: {
    /**
     * 获取未读通知数量
     */
    async fetchUnreadCount() {
      try {
        const data = await getNotificationUnreadCount()
        this.unreadCount = Number(data.total) || 0
      } catch (e) {
        // 静默失败，不影响顶栏其他功能
      }
    },
    /**
     * 跳转通知列表页
     */
    handleGoNotification() {
      if (this.$route.path !== '/notification/list') {
        this.$router.push('/notification/list')
      }
    },
    /**
     * 切换侧边栏折叠状态
     */
    handleToggleSidebar() {
      this.$store.dispatch('app/toggleSidebar')
    },
    /**
     * 处理下拉菜单命令
     * @param {string} command
     */
    handleCommand(command) {
      if (command === 'logout') {
        this.handleLogout()
      } else if (command === 'password') {
        this.passwordDialogVisible = true
      }
    },
    /**
     * 退出登录
     */
    handleLogout() {
      this.$confirm('确定要退出登录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        await this.$store.dispatch('user/logout')
        this.$router.push('/login')
        this.$message.success('已退出登录')
      }).catch(() => {})
    },
    /**
     * 提交修改密码
     */
    async handleChangePassword() {
      try {
        await this.$refs.passwordForm.validate()
        this.submitLoading = true
        await changePassword(this.passwordForm)
        this.$message.success('密码修改成功，请重新登录')
        this.passwordDialogVisible = false
        await this.$store.dispatch('user/logout')
        this.$router.push('/login')
      } catch (e) {
        // 校验失败或接口错误
      } finally {
        this.submitLoading = false
      }
    },
    /**
     * 密码弹窗关闭后重置表单
     */
    handlePasswordDialogClosed() {
      this.$refs.passwordForm.resetFields()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: $header-height;
  background: $card-bg;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  padding: 0 20px;
  flex-shrink: 0;
  z-index: 99;

  &__left {
    display: flex;
    align-items: center;
  }

  &__collapse-btn {
    font-size: 20px;
    color: $text-regular;
    cursor: pointer;
    margin-right: 16px;

    &:hover {
      color: $primary-color;
    }
  }

  &__breadcrumb {
    line-height: $header-height;
  }

  &__breadcrumb-current {
    color: $primary-color;
    font-weight: 600;
  }

  &__right {
    display: flex;
    align-items: center;
  }

  &__notify {
    margin-right: 20px;
    line-height: 1;
  }

  &__notify-icon {
    font-size: 20px;
    color: $text-regular;
    cursor: pointer;

    &:hover {
      color: $primary-color;
    }
  }

  &__user {
    display: flex;
    align-items: center;
    cursor: pointer;
    color: $text-regular;

    &:hover {
      color: $primary-color;
    }
  }

  &__user-avatar {
    font-size: 22px;
    color: $primary-color;
    margin-right: 6px;
  }

  &__user-name {
    font-size: 14px;
    margin-right: 4px;
  }
}
</style>
